import { InitPage } from "../index";
import swal from "sweetalert";
import * as Toastr from "toastr";

declare global {
  interface Window {
    currentNTOTransferOrders?: NTOTransferOrders;
  }
}

interface TransferOrderConfig {
  transferId: string;
  tenantCode: string;
  transferTenantCodes: string[];
  loadPalletsUrl: string;
  loadCartonsItemsUrl: string;
  selectedContainers: number[];
  mergeUrl?: string;
}

interface Counters {
  total_items_count: number;
  total_items_qty: number;
  sent_items_count: number;
  sent_items_qty: number;
  received_items_count: number;
  received_items_qty: number;
  partially_received_items_count: number;
  partially_received_items_qty: number;
  excluded_items_count: number;
  excluded_items_qty: number;
  cartons_count: number;
}

interface CartonsResponse {
  cartons_html: string;
  counters: Counters;
}

class NTOTransferOrders {
  private transferId: string;
  private tenantCode: string;
  private transferTenantCodes: string[];
  private loadPalletsUrl: string;
  private loadCartonsItemsUrl: string;
  private selectedContainers: number[];
  private selectedPallets: number[];
  private selectedStatus: string;
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  private mergeUrl: string;

  constructor(config: TransferOrderConfig) {
    this.transferId = config.transferId;
    this.tenantCode = config.tenantCode;
    this.transferTenantCodes =
      config.transferTenantCodes ||
      (config.tenantCode ? [config.tenantCode] : []);
    this.loadPalletsUrl = config.loadPalletsUrl;
    this.loadCartonsItemsUrl = config.loadCartonsItemsUrl;
    this.selectedContainers = config.selectedContainers;
    this.selectedPallets = [];
    this.selectedStatus = "";
    this.mergeUrl = config.mergeUrl || "";
  }

  public init(): void {
    this.loadInitialData();
    this.setupEventHandlers();
    this.bindModalEvents();
    this.setupDeleteHandlers();
    this.updateBulkDeleteButtonState();
    this.setupButtonClickPrevention();
    this.setupMergeHandlers();
    this.setupTransferProgressChannel();
  }

  private setupTransferProgressChannel(): void {
    if (!this.transferId || this.transferTenantCodes.length === 0) {
      return;
    }

    const self = this;
    const cable = (window as any).App && (window as any).App.cable;

    if (!cable) {
      return;
    }

    this.transferTenantCodes.forEach((tenantCode) => {
      cable.subscriptions.create(
        {
          channel: "NTO::TransferProgressChannel",
          tenant_code: tenantCode,
          transfer_id: this.transferId,
        },
        {
          connected() {
            console.log(
              `Connected to NTO Transfer Progress Channel (${tenantCode})`,
            );
          },
          disconnected() {
            console.log(
              `Disconnected from NTO Transfer Progress Channel (${tenantCode})`,
            );
          },
          received(data: any) {
            self.updateTransferProgressBar(data);
          },
        },
      );
    });
  }

  private updateTransferProgressBar(data: any): void {
    const container = $("#nto-transfer-progress-container");
    const progressBar = $("#progressBar");
    const label = $(".progress-label");

    if (data.state === "processing") {
      container.show();
      progressBar.val(data.progress);
      label.text(data.message + ": " + data.progress + "%");
    } else if (data.state === "completed") {
      container.show();
      progressBar.val(100);
      label.text(data.message + ": 100%");

      // Hide after 3 seconds and reload
      setTimeout(() => {
        container.fadeOut(() => {
          window.location.reload();
        });
      }, 3000);
    }
  }

  private loadInitialData(): void {
    // Only load pallets if we have a valid transferId (show page)
    if (this.transferId) {
      this.loadPallets();
    }
    this.updateStatusFilterUI();
    this.toggleClearButton();
  }

  private loadPallets(): void {
    const containerIds = this.selectedContainers.join(",");

    $.ajax({
      url: this.loadPalletsUrl,
      method: "GET",
      data: {
        transfer_id: this.transferId,
        container_ids: containerIds,
      },
      beforeSend: () => {
        $("#pallets-container").html(
          '<div class="loading-spinner text-center" style="padding: 20px;"><i class="fa fa-spinner fa-spin fa-2x"></i><p>Loading pallets...</p></div>',
        );
      },
      success: (data: string) => {
        $("#pallets-container").html(data);
        this.setupPalletHandlers();
        this.updatePalletSelection();
      },
      error: () => {
        $("#pallets-container").html(
          '<div class="alert alert-warning">Error loading pallets</div>',
        );
      },
    });
  }

  private loadCartonsItems(): Promise<void> {
    const containerIds = this.selectedContainers.join(",");
    const palletIds = this.selectedPallets.join(",");
    const searchTerm = $("#search-input").val() as string;

    return new Promise((resolve, reject) => {
      $.ajax({
        url: this.loadCartonsItemsUrl,
        method: "GET",
        data: {
          transfer_id: this.transferId,
          container_ids: containerIds,
          pallet_ids: palletIds,
          search: searchTerm,
          status: this.selectedStatus,
        },
        dataType: "json",
        beforeSend: () => {
          const advancedTransferOrders =
            (window as any).advanced_transfer_orders || false;
          const loadingMessage = advancedTransferOrders
            ? "Loading cartons, items, and assets..."
            : "Loading items and assets...";
          $("#cartons-items-container").html(
            `<div class="loading-spinner text-center" style="padding: 40px;"><i class="fa fa-spinner fa-spin fa-3x"></i><p>${loadingMessage}</p></div>`,
          );
        },
        success: (response: CartonsResponse) => {
          $("#cartons-items-container").html(response.cartons_html);
          this.updateCounters(response.counters);
          this.setupCartonsItemsHandlers();
          this.setupDeleteHandlers();
          this.updateBulkDeleteButtonState();
          resolve();
        },
        error: () => {
          const advancedTransferOrders =
            (window as any).advanced_transfer_orders || false;
          const errorMessage = advancedTransferOrders
            ? "Error loading cartons and items"
            : "Error loading items";
          $("#cartons-items-container").html(
            `<div class="alert alert-warning">${errorMessage}</div>`,
          );
          reject();
        },
      });
    });
  }

  private updateCounters(counters: Counters): void {
    $("#total-items-count").text(counters.total_items_count);
    $("#total-items-qty").text(counters.total_items_qty);
    $("#sent-items-count").text(counters.sent_items_count);
    $("#sent-items-qty").text(counters.sent_items_qty);
    $("#received-items-count").text(counters.received_items_count);
    $("#received-items-qty").text(counters.received_items_qty);
    $("#partially-received-items-count").text(
      counters.partially_received_items_count,
    );
    $("#partially-received-items-qty").text(
      counters.partially_received_items_qty,
    );
    $("#excluded-items-count").text(counters.excluded_items_count);
    $("#excluded-items-qty").text(counters.excluded_items_qty);
  }

  private updateStatusFilterUI(): void {
    $(".status-filter-link").removeClass("active");
    if (this.selectedStatus === "") {
      $('.status-filter-link[data-status=""]').addClass("active");
    } else {
      $(
        '.status-filter-link[data-status="' + this.selectedStatus + '"]',
      ).addClass("active");
    }
  }

  private toggleClearButton(): void {
    const searchInput = $("#search-input");
    if (searchInput.length === 0) {
      // Element doesn't exist on this page (likely index page), skip
      return;
    }

    const searchValue = searchInput.val() as string;
    if (
      searchValue &&
      searchValue.trim() === "" &&
      this.selectedStatus === ""
    ) {
      $("#clear-search-btn").hide();
    } else {
      $("#clear-search-btn").show();
    }
  }

  private setupEventHandlers(): void {
    // Only set up container-specific handlers if we have elements that exist (show page)
    if ($("#select_all_container").length > 0) {
      $("#select_all_container").on("click", () => {
        const checkAll = $("#select_all_container").is(":checked");
        $(".container").prop("checked", checkAll);
        this.updateContainerSelection();
      });
    }

    $(document).on("click", ".container", () => {
      const selectedCount = $(".container:checked").length;
      const enabledCount = $(".container:enabled").length;

      if (!$(".container").is(":checked")) {
        $("#select_all_container").prop("checked", false);
      } else if (selectedCount === enabledCount) {
        $("#select_all_container").prop("checked", true);
      }

      this.updateContainerSelection();
    });

    // Search input handler with debouncing
    $(document).on("input", "#search-input", () => {
      this.toggleClearButton();
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        if (this.transferId) {
          this.loadCartonsItems();
        }
      }, 500);
    });

    // Search form submission
    $(document).on("submit", "#search-form", (e) => {
      e.preventDefault();
      if (this.transferId) {
        this.loadCartonsItems();
      }
    });

    // Status filter links
    $(document).on("click", ".status-filter-link", (e) => {
      e.preventDefault();
      this.selectedStatus = $(e.currentTarget).data("status");
      this.updateStatusFilterUI();
      this.toggleClearButton();
      if (this.transferId) {
        this.loadCartonsItems();
      }
    });

    // Clear search functionality
    $(document).on("click", "#clear-search-btn", (e) => {
      e.preventDefault();
      $("#search-input").val("");
      this.selectedStatus = "";
      this.updateStatusFilterUI();
      this.toggleClearButton();
      if (this.transferId) {
        this.loadCartonsItems();
      }
    });

    // Other existing handlers
    $(document).on("click", ".print-icon", (e) => {
      if (!$(e.currentTarget).data("id")) return;
      $("#re-print-action-modal").modal("show");
    });

    // Handle reprint form submission via AJAX
    $(document).on("submit", "#reprint_action_form", (e) => {
      e.preventDefault();
      this.handleReprintSubmission();
    });

    // Handle reclassify form submission via AJAX
    $(document).on("submit", "#reclassify_form", (e) => {
      e.preventDefault();
      this.handleReclassifySubmission();
    });

    $(document).on("show.bs.modal", "#bulkClose", (e) => {
      if (e.target !== e.currentTarget) return;
      this.applyBulkCloseRules();
    });

    $(document).on("submit", "#bulk_close_form", (e) => {
      e.preventDefault();
      this.handleBulkCloseSubmission();
    });

    // Handle bulk expected by update form submission via AJAX
    $(document).on("submit", "#bulk_expected_by_form", (e) => {
      e.preventDefault();
      this.handleBulkExpectedBySubmission();
    });

    // Handle undo button clicks with custom modal confirmation
    $(document).on("click", ".undo-record:not(.disabled)", (e) => {
      e.preventDefault();
      const button = $(e.currentTarget);
      const row = button.closest("tr");
      const undoLink = row.find(".undo-link");

      if (undoLink.length === 0) {
        console.error("Undo link not found");
        return;
      }

      // Get the undo URL from the hidden link
      const undoUrl = undoLink.attr("href");
      if (!undoUrl) {
        console.error("Undo URL not found");
        return;
      }

      // Store references for the modal
      $("#confirm-undo-btn").data("undo-url", undoUrl);
      $("#confirm-undo-btn").data("original-button", button);

      // Store reference to this instance for the modal callback
      window.currentNTOTransferOrders = this;

      // Show custom modal
      $("#undoConfirmationModal").modal("show");
    });

    // Handle add note form submission via AJAX
    $(document).on("submit", "#add-note-form", (e) => {
      e.preventDefault();
      e.stopImmediatePropagation(); // Add this line
      this.handleAddNoteSubmission();
    });

    // Handle EOS sync retry button clicks
    $(document).on("click", "#retry-eos-sync-btn", (e) => {
      e.preventDefault();
      this.handleEosSyncRetry(e);
    });

    $('input[name="export_type"]').on("change", function () {
      const activeOption = $(this).val() as string;
      if (activeOption === "custom")
        $("#custom_export").prop("disabled", false);
      else $("#custom_export").prop("disabled", true);
    });

    $(".select-all-tags").on("click", function () {
      const checkAll = $(this).is(":checked");
      const itemId = $(this).data("item-id");
      $(`.asset-item-${itemId}`).prop("checked", checkAll);
    });

    $(".asset").on("click", function () {
      const itemId = $(this)
        .attr("class")
        .match(/asset-item-(\d+)/)?.[1];
      if (itemId) {
        const selectedCount = $(`.asset-item-${itemId}:checked`).length;
        const enabledCount = $(`.asset-item-${itemId}:enabled`).length;
        const selectAllCheckbox = $(
          `.select-all-tags[data-item-id="${itemId}"]`,
        );

        if (!$(this).is(":checked")) {
          selectAllCheckbox.prop("checked", false);
        } else if (selectedCount === enabledCount) {
          selectAllCheckbox.prop("checked", true);
        }
      }
    });

    $(".view-icon").on("click", function () {
      if ($(this).hasClass("print-preview-icon")) return;

      const type = $(this).data("type");
      if (type == 4) {
        $(".custom-eos-header").show();
        $(".custom-eos-body").show();
      } else {
        $(".custom-eos-header").hide();
        $(".custom-eos-body").hide();
      }
      $("#showDetails").modal("show");
    });

    $(".change_dst").on("click", function () {
      $("#changeLocation").modal("show");
    });

    $(".accordion-arrow-icon").on("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("fa-chevron-down")) {
        target.classList.remove("fa-chevron-down");
        target.classList.add("fa-chevron-up");
      } else {
        target.classList.add("fa-chevron-down");
        target.classList.remove("fa-chevron-up");
      }
    });

    // $('.export-btn').on('click', function() {
    //   $("#export-dialog").modal('show');
    // });
  }

  private setupPalletHandlers(): void {
    $("#select_all_pallet").on("click", () => {
      const checkAll = $("#select_all_pallet").is(":checked");
      $(".pallet").prop("checked", checkAll);
      this.updatePalletSelection();
    });

    $(".pallet").on("click", () => {
      const selectedCount = $(".pallet:checked").length;
      const enabledCount = $(".pallet:enabled").length;

      if (!$(".pallet").is(":checked")) {
        $("#select_all_pallet").prop("checked", false);
      } else if (selectedCount === enabledCount) {
        $("#select_all_pallet").prop("checked", true);
      }

      this.updatePalletSelection();
    });
  }

  private setupCartonsItemsHandlers(): void {
    // Re-setup accordion handlers for newly loaded content
    $(".accordion-arrow-icon")
      .off("click")
      .on("click", (e) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains("fa-chevron-down")) {
          target.classList.remove("fa-chevron-down");
          target.classList.add("fa-chevron-up");
        } else {
          target.classList.add("fa-chevron-down");
          target.classList.remove("fa-chevron-up");
        }
      });

    // Re-setup select all functionality for dynamically loaded content
    $(".select-all-tags")
      .off("click")
      .on("click", (e) => {
        const checkAll = $(e.target).is(":checked");
        const itemId = $(e.target).data("item-id");
        // Only check/uncheck enabled checkboxes
        $(`.asset-item-${itemId}:enabled`).prop("checked", checkAll);

        // Update bulk delete button state
        this.updateBulkDeleteButtonState();
      });

    $(".asset")
      .off("click")
      .on("click", (e) => {
        const itemId = $(e.target)
          .attr("class")
          .match(/asset-item-(\d+)/)?.[1];
        if (itemId) {
          const selectedCount = $(`.asset-item-${itemId}:checked`).length;
          const enabledCount = $(`.asset-item-${itemId}:enabled`).length;
          const selectAllCheckbox = $(
            `.select-all-tags[data-item-id="${itemId}"]`,
          );

          if (!$(e.target).is(":checked")) {
            selectAllCheckbox.prop("checked", false);
          } else if (selectedCount === enabledCount) {
            selectAllCheckbox.prop("checked", true);
          }
        }

        // Update bulk delete button state
        this.updateBulkDeleteButtonState();
      });
  }

  private updateContainerSelection(): void {
    this.selectedContainers = [];
    $(".container:checked").each((index, element) => {
      this.selectedContainers.push(parseInt($(element).val() as string));
    });

    // Reload pallets when containers change
    this.loadPallets();
  }

  private updateBulkDeleteButtonState(): void {
    const selectedAssets = $(".asset:checked");
    const bulkDeleteBtn = $("#bulk-delete-tags-btn");

    if (selectedAssets.length === 0) {
      bulkDeleteBtn.addClass("disabled");
      bulkDeleteBtn.prop("disabled", true);
    } else {
      bulkDeleteBtn.removeClass("disabled");
      bulkDeleteBtn.prop("disabled", false);
    }
  }

  private updatePalletSelection(reloadCartons: boolean = true): void {
    this.selectedPallets = [];
    $(".pallet:checked").each((index, element) => {
      this.selectedPallets.push(parseInt($(element).val() as string));
    });

    // Reload cartons and items when pallets change (unless this is initial load)
    if (reloadCartons) {
      this.loadCartonsItems();
    }
  }

  private bindModalEvents(): void {
    // Only set up modal events if we have a transferId (show page)
    if (!this.transferId) {
      return;
    }

    // Handle view-icon clicks to load transaction details
    $(document).on("click", ".view-icon", (e) => {
      e.preventDefault();
      const $link = $(e.currentTarget);
      const logId = $link.data("log-id");
      const modalTarget = $link.data("modal-target");

      if (logId && modalTarget) {
        this.loadTransactionDetails(logId, modalTarget);
      }
    });

    // Handle print-preview-icon clicks to load a carton label preview
    $(document).on("click", ".print-preview-icon", (e) => {
      e.preventDefault();
      const logId = $(e.currentTarget).data("log-id");

      if (logId) {
        this.loadPrintPreview(logId);
      }
    });
  }

  private loadPrintPreview(logId: string): void {
    const url = `/${this.getClientId()}/${this.getStoreId()}/nto/transfer_orders/${this.transferId}/logs/${logId}/preview`;

    $("#printPreview").modal("show");
    $("#print-preview-content").html(`
      <div class="text-center">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
        <p>Loading label preview...</p>
      </div>
    `);

    $.ajax({
      url: url,
      method: "GET",
      dataType: "json",
      success: (data) => {
        const imageHtml = data.image
          ? `<div class="text-center" style="margin-bottom: 15px"><img src="${data.image}" style="max-width: 100%; border: 1px solid #ddd" /></div>`
          : `<div class="alert alert-info text-center">Label image preview is unavailable.</div>`;
        const zplHtml = `<pre style="max-height: 250px; overflow: auto; background: #f5f5f5; padding: 10px; white-space: pre-wrap">${this.escapeHtml(
          data.zpl || ""
        )}</pre>`;
        $("#print-preview-content").html(imageHtml + zplHtml);
      },
      error: (xhr) => {
        console.error("Error loading label preview:", xhr);
        $("#print-preview-content").html(`
          <div class="alert alert-warning text-center">
            <i class="fa fa-exclamation-triangle"></i>
            Error loading label preview
          </div>
        `);
      },
    });
  }

  private escapeHtml(value: string): string {
    return $("<div>").text(value).html();
  }

  private loadTransactionDetails(logId: string, modalTarget: string): void {
    const url = `/${this.getClientId()}/${this.getStoreId()}/nto/transfer_orders/${this.transferId}/logs/${logId}/details`;

    // Show modal with loading state
    $(modalTarget).modal("show");

    // Fetch transaction details as HTML
    $.ajax({
      url: url,
      method: "GET",
      dataType: "html",
      success: (html) => {
        $("#transaction-details-content").html(html);
      },
      error: (xhr) => {
        console.error("Error loading transaction details:", xhr);
        this.showError("Error loading transaction details");
      },
    });
  }

  private showError(message: string): void {
    const content = $("#transaction-details-content");
    content.html(`
      <div class="alert alert-warning text-center">
        <i class="fa fa-exclamation-triangle"></i>
        ${message}
      </div>
    `);
  }

  private getClientId(): string {
    // Extract client ID from URL or data attributes
    const pathParts = window.location.pathname.split("/");
    return pathParts[1] || "";
  }

  private getStoreId(): string {
    // Extract store ID from URL or data attributes
    const pathParts = window.location.pathname.split("/");
    return pathParts[2] || "";
  }

  private setupDeleteHandlers(): void {
    // Re-setup delete handlers for dynamically loaded content
    $(document)
      .off("click", ".deleteRecord")
      .on("click", ".deleteRecord", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const message =
          $(e.currentTarget).data("delete-message") || "Are you sure?";

        // @ts-ignore
        swal({
          title: "Are you sure?",
          text: message,
          icon: "warning",
          closeOnClickOutside: false,
          buttons: {
            cancel: {
              text: "No",
              value: null,
              visible: true,
              className: "sn-btn sn-btn-sm sn-btn-silver",
              closeModal: true,
            },
            confirm: {
              text: "Yes",
              value: true,
              visible: true,
              className: "sn-btn sn-btn-sm sn-btn-red",
              closeModal: true,
            },
          },
          dangerMode: true,
        }).then((willDelete) => {
          if (willDelete) {
            const $current = $(e.currentTarget);

            // Check if this is a bulk delete button
            if (
              $current.attr("id") === "bulk-delete-btn" ||
              $current.text().includes("Bulk Delete")
            ) {
              this.handleBulkDelete();
              return;
            }

            // Handle individual asset/item deletion/exclude/include via AJAX
            const $target = $current.next('a[style*="display: none"]');
            let actionUrl = "";
            let actionMethod = $target.attr("data-method") || "DELETE";

            if ($target.length) {
              actionUrl = $target.attr("href");
              // Check if it's a PATCH request (exclude/include) or DELETE (delete)
              if (
                $target.attr("data-method") === "patch" ||
                $target.attr("data-method") === "PATCH"
              ) {
                actionMethod = "PATCH";
              }
            } else {
              const $hiddenLink = $current
                .closest("td")
                .find('a[style*="display: none"]');
              if ($hiddenLink.length) {
                actionUrl = $hiddenLink.attr("href");
                if (
                  $hiddenLink.attr("data-method") === "patch" ||
                  $hiddenLink.attr("data-method") === "PATCH"
                ) {
                  actionMethod = "PATCH";
                }
              }
            }

            if (actionUrl) {
              // Check if this is a download action
              if ($current.hasClass("download-icon")) {
                // For download actions, just follow the link
                window.location.href = actionUrl;
              } else if (
                $current.hasClass("delete-icon") &&
                $current.data("note-id")
              ) {
                this.handleNoteDeletion(
                  actionUrl,
                  actionMethod,
                  $current.data("note-id"),
                );
              } else {
                this.handleIndividualAction(actionUrl, actionMethod);
              }
            }
          }
        });
      });
  }

  private handleBulkDelete(): void {
    // Check if button is disabled
    const bulkDeleteBtn = $("#bulk-delete-tags-btn");
    if (bulkDeleteBtn.hasClass("disabled") || bulkDeleteBtn.prop("disabled")) {
      return;
    }

    // Get all selected asset checkboxes
    const selectedAssets = $(".asset:checked");

    if (selectedAssets.length === 0) {
      swal({
        title: "No Selection",
        text: "Please select at least one asset to delete.",
        icon: "warning",
      });
      return;
    }

    // Store the current accordion state before making the request
    const expandedAccordions = this.getExpandedAccordionStates();

    // Collect asset IDs
    const assetIds = [];
    selectedAssets.each(function () {
      assetIds.push($(this).val());
    });

    // Get the bulk delete URL
    const bulkDeleteLink = $("#bulk-delete-link");
    const currentHref = bulkDeleteLink.attr("href");
    const baseUrl = currentHref.split("?")[0];

    // Show loading state
    bulkDeleteBtn.prop("disabled", true).text("Deleting...");

    $.ajax({
      url: baseUrl,
      method: "DELETE",
      data: { asset_ids: assetIds.join(",") },
      dataType: "json",
      success: (response) => {
        if (response.success) {
          // Show success message with toastr
          Toastr.success(response.message, "Success");

          // Refresh the cartons and items data
          this.loadCartonsItems().then(() => {
            // Restore accordion states after data is loaded
            this.restoreAccordionStates(expandedAccordions);
          });
        } else {
          swal({
            title: "Error",
            text: response.message,
            icon: "error",
          });
        }
      },
      error: (xhr) => {
        let errorMessage = "An error occurred while deleting assets.";

        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message;
        } else if (xhr.status === 404) {
          errorMessage = "Assets not found.";
        } else if (xhr.status === 422) {
          errorMessage = "Invalid data provided.";
        } else if (xhr.status === 403) {
          errorMessage = "You do not have permission to delete assets.";
        }

        swal({
          title: "Error",
          text: errorMessage,
          icon: "error",
        });
      },
      complete: () => {
        // Reset button state
        bulkDeleteBtn.prop("disabled", false).text("Bulk Delete");
        // Update button state based on current selection
        this.updateBulkDeleteButtonState();
      },
    });
  }

  private handleIndividualAction(
    actionUrl: string,
    method: string = "DELETE",
  ): void {
    // Store the current accordion state before making the request
    const expandedAccordions = this.getExpandedAccordionStates();

    $.ajax({
      url: actionUrl,
      method: method,
      dataType: "json",
      success: (response) => {
        if (response.success) {
          // Show success message with toastr
          Toastr.success(response.message, "Success");

          // Refresh the cartons and items data
          this.loadCartonsItems().then(() => {
            // Restore accordion states after data is loaded
            this.restoreAccordionStates(expandedAccordions);
          });
        } else {
          swal({
            title: "Error",
            text: response.message,
            icon: "error",
          });
        }
      },
      error: (xhr) => {
        let errorMessage = "An error occurred while processing the request.";
        let actionType = "processing";

        if (method === "DELETE") {
          errorMessage = "An error occurred while deleting the item.";
          actionType = "delete";
        } else if (method === "PATCH") {
          errorMessage = "An error occurred while updating the item status.";
          actionType = "update";
        }

        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message;
        } else if (xhr.status === 404) {
          errorMessage = "Item not found.";
        } else if (xhr.status === 422) {
          errorMessage = "Invalid data provided.";
        } else if (xhr.status === 403) {
          errorMessage = `You do not have permission to ${actionType} this item.`;
        }

        swal({
          title: "Error",
          text: errorMessage,
          icon: "error",
        });
      },
    });
  }

  private getExpandedAccordionStates(): string[] {
    const expandedStates: string[] = [];

    // Find all accordion headers that are currently expanded
    $(".accordion-arrow-icon").each(function () {
      const $icon = $(this);
      const $header = $icon.closest('[data-toggle="collapse"]');
      const targetId = $header.attr("data-target") || $header.attr("href");

      if (targetId && $(targetId).hasClass("in")) {
        expandedStates.push(targetId);
      }
    });

    return expandedStates;
  }

  private restoreAccordionStates(expandedStates: string[]): void {
    // Wait a bit for the DOM to be fully updated
    setTimeout(() => {
      expandedStates.forEach((targetId) => {
        const $target = $(targetId);
        const $header = $(`[data-target="${targetId}"], [href="${targetId}"]`);
        const $icon = $header.find(".accordion-arrow-icon");

        if ($target.length && $header.length && $icon.length) {
          // Expand the accordion
          $target.addClass("in");
          $target.css("height", "auto");

          // Update the arrow icon to point up
          $icon.removeClass("fa-chevron-down").addClass("fa-chevron-up");
        }
      });
    }, 100);
  }

  private handleReprintSubmission(): void {
    const form = $("#reprint_action_form");
    const submitBtn = $("#submit-print_action-form");
    const statusMessage = $("#reprint-status-message");
    const formData = form.serialize();

    // Show loading state
    submitBtn.prop("disabled", true).val("Printing...");
    statusMessage.hide();

    $.ajax({
      url: form.attr("action"),
      method: "POST",
      data: formData,
      dataType: "json",
      success: (response) => {
        if (response.success) {
          statusMessage
            .removeClass("alert-warning")
            .addClass("alert-success")
            .html(`<i class="fa fa-check"></i> ${response.message}`)
            .show();

          // Close modal after a short delay
          setTimeout(() => {
            $("#re-print-action-modal").modal("hide");
            statusMessage.hide();
          }, 2000);
        } else {
          statusMessage
            .removeClass("alert-success")
            .addClass("alert-warning")
            .html(
              `<i class="fa fa-exclamation-triangle"></i> ${response.message}`,
            )
            .show();
        }
      },
      error: (xhr) => {
        let errorMessage =
          "An error occurred while processing the reprint request.";

        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message;
        } else if (xhr.status === 404) {
          errorMessage = "Asset not found.";
        } else if (xhr.status === 422) {
          errorMessage = "Invalid data provided.";
        }

        statusMessage
          .removeClass("alert-success")
          .addClass("alert-warning")
          .html(`<i class="fa fa-exclamation-triangle"></i> ${errorMessage}`)
          .show();
      },
      complete: () => {
        submitBtn.prop("disabled", false).val("Reprint");
      },
    });
  }

  private handleReclassifySubmission(): void {
    const form = $("#reclassify_form");
    const submitBtn = $("#reclassify-button");
    const statusMessage = $("#reclassify-status-message");
    const formData = form.serialize();

    // Validate form
    const locationCode = $('select[name="location_code"]').val();
    if (!locationCode) {
      statusMessage
        .removeClass("alert-success")
        .addClass("alert-warning")
        .html(
          '<i class="fa fa-exclamation-triangle"></i> Please select a location.',
        )
        .show();
      return;
    }

    // Show loading state
    submitBtn.prop("disabled", true).text("Changing...");
    statusMessage.hide();

    $.ajax({
      url: form.attr("action"),
      method: "POST",
      data: formData,
      dataType: "json",
      success: (response) => {
        if (response.success) {
          statusMessage
            .removeClass("alert-warning")
            .addClass("alert-success")
            .html(`<i class="fa fa-check"></i> ${response.message}`)
            .show();

          // Close modal after a short delay and redirect or reload
          setTimeout(() => {
            $("#changeLocation").modal("hide");
            // Use redirect_url if provided, otherwise reload the page
            if (response.redirect_url) {
              window.location.href = response.redirect_url;
            } else {
              window.location.reload();
            }
          }, 2000);
        } else {
          statusMessage
            .removeClass("alert-success")
            .addClass("alert-warning")
            .html(
              `<i class="fa fa-exclamation-triangle"></i> ${response.message}`,
            )
            .show();
        }
        statusMessage.show();
      },
      error: (xhr) => {
        let errorMessage = "An error occurred while changing the destination.";

        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message;
        } else if (xhr.status === 404) {
          errorMessage = "Transfer order not found.";
        } else if (xhr.status === 422) {
          errorMessage = "Invalid data provided.";
        } else if (xhr.status === 403) {
          errorMessage =
            "You do not have permission to change the destination.";
        }

        statusMessage
          .removeClass("alert-success")
          .addClass("alert-warning")
          .html(`<i class="fa fa-exclamation-triangle"></i> ${errorMessage}`)
          .show();
        statusMessage.show();
      },
      complete: () => {
        // Reset button state
        submitBtn.prop("disabled", false).text("Change Destination");
      },
    });
  }

  private applyBulkCloseRules(): void {
    if ($("#selected_transfers_input").length === 0) return;

    const selected = $(".transfer:checked");
    const anyInbound = selected.filter((_, el) => $(el).data("inbound") === true).length > 0;
    const anyReceived = selected.filter((_, el) => $(el).data("received") === true).length > 0;
    const anyUndone = selected.filter((_, el) => $(el).data("status") === "undo").length > 0;

    const undoRadio = $('input[name="type"][value="undo"]');
    const undoLocked = undoRadio.data("locked") === true || anyReceived || anyUndone;
    undoRadio.prop("disabled", undoLocked);
    $("#nto-close-undo-label").toggleClass("not-allowed", undoLocked).toggleClass("pointer", !undoLocked);
    $("#nto-close-undo-row").toggle(!anyInbound);

    if ((undoLocked || anyInbound) && undoRadio.is(":checked")) {
      $('input[name="type"][value="change_status"]').prop("checked", true).trigger("change");
    }
  }

  private handleBulkCloseSubmission(): void {
    const form = $("#bulk_close_form");
    const submitBtn = form.find('input[type="submit"]') as JQuery<HTMLElement>;
    const statusMessage = $("#close-status-message");

    // Check if this is index page (multiple transfers) or show page (single transfer)
    const selectedTransfersInput = $("#selected_transfers_input");
    if (selectedTransfersInput.length > 0) {
      // Index page - populate selected transfers
      const selectedTransfers: string[] = [];
      $(".transfer:checked").each(function () {
        selectedTransfers.push($(this).val() as string);
      });

      if (selectedTransfers.length === 0) {
        statusMessage
          .removeClass("alert-success")
          .addClass("alert-warning")
          .html(
            '<i class="fa fa-exclamation-triangle"></i> Please select at least one transfer order.',
          )
          .show();
        return;
      }

      selectedTransfersInput.val(selectedTransfers.join(","));
    }

    const formData = form.serialize();

    // Validate form based on selected type
    const closeType = $('input[name="type"]:checked').val();
    const pinNumber = $("#nto_pin_number").val();
    const status = $("#status").val();

    if (closeType === "force_receive" && !pinNumber) {
      statusMessage
        .removeClass("alert-success")
        .addClass("alert-warning")
        .html(
          '<i class="fa fa-exclamation-triangle"></i> PIN number is required for force receive operation.',
        )
        .show();
      return;
    }

    if (closeType === "change_status" && !status) {
      statusMessage
        .removeClass("alert-success")
        .addClass("alert-warning")
        .html(
          '<i class="fa fa-exclamation-triangle"></i> Please select a status.',
        )
        .show();
      return;
    }

    // Submit form directly without confirmation
    this.submitBulkCloseForm(form, submitBtn, statusMessage, formData);
  }

  private submitBulkCloseForm(
    form: JQuery,
    submitBtn: JQuery,
    statusMessage: JQuery,
    formData: string,
  ): void {
    // Show loading state
    submitBtn.prop("disabled", true);
    statusMessage.hide();

    $.ajax({
      url: form.attr("action"),
      method: "POST",
      data: formData,
      dataType: "json",
      success: (response) => {
        if (response.success) {
          statusMessage
            .removeClass("alert-warning")
            .addClass("alert-success")
            .html(`<i class="fa fa-check"></i> ${response.message}`)
            .show();

          // Close modal after a short delay and reload page
          setTimeout(() => {
            $("#bulkClose").modal("hide");
            // Reload the current page
            window.location.reload();
          }, 2000);
        } else {
          statusMessage
            .removeClass("alert-success")
            .addClass("alert-warning")
            .html(
              `<i class="fa fa-exclamation-triangle"></i> ${response.message}`,
            )
            .show();
        }
        statusMessage.show();
      },
      error: (xhr) => {
        let errorMessage =
          "An error occurred while closing the transfer order.";

        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message;
        } else if (xhr.status === 404) {
          errorMessage = "Transfer order not found.";
        } else if (xhr.status === 422) {
          errorMessage = "Invalid data provided.";
        } else if (xhr.status === 403) {
          errorMessage =
            "You do not have permission to close the transfer order.";
        }

        statusMessage
          .removeClass("alert-success")
          .addClass("alert-warning")
          .html(`<i class="fa fa-exclamation-triangle"></i> ${errorMessage}`)
          .show();
        statusMessage.show();
      },
      complete: () => {
        // Reset button state
        submitBtn.prop("disabled", false).val("Confirm");
      },
    });
  }

  private handleBulkExpectedBySubmission(): void {
    const form = $("#bulk_expected_by_form");
    const submitBtn = $("#bulk-expected-by-submit");
    const statusMessage = $("#expected-by-status-message");
    const formData = form.serialize();

    // Show loading state
    submitBtn.prop("disabled", true).text("Updating...");
    statusMessage.hide();

    $.ajax({
      url: form.attr("action"),
      method: "POST",
      data: formData,
      dataType: "json",
      success: (response) => {
        if (response.success) {
          statusMessage
            .removeClass("alert-warning alert-danger")
            .addClass("alert-success")
            .html('<i class="fa fa-check"></i> ' + response.message)
            .show();

          // Close modal after a short delay and reload page
          setTimeout(() => {
            $("#bulk_expected_by_update").modal("hide");
            window.location.reload();
          }, 2000);
        } else {
          statusMessage
            .removeClass("alert-success")
            .addClass("alert-warning")
            .html(
              '<i class="fa fa-exclamation-triangle"></i> ' + response.message,
            )
            .show();
          submitBtn.prop("disabled", false).text("Yes");
        }
      },
      error: (xhr) => {
        let errorMessage = "An error occurred while updating expected by date.";

        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message;
        } else if (xhr.status === 404) {
          errorMessage = "Transfer order(s) not found.";
        } else if (xhr.status === 422) {
          errorMessage = "Invalid data provided.";
        } else if (xhr.status === 403) {
          errorMessage =
            "You do not have permission to update expected by date.";
        } else if (xhr.status === 500) {
          errorMessage = "Server error occurred, please try again.";
        }

        statusMessage
          .removeClass("alert-success alert-warning")
          .addClass("alert-danger")
          .html('<i class="fa fa-exclamation-triangle"></i> ' + errorMessage)
          .show();
        submitBtn.prop("disabled", false).text("Yes");
      },
    });
  }

  private handleUndoSubmission(button: JQuery): void {
    const row = button.closest("tr");
    const undoLink = row.find(".undo-link");
    const statusMessage = $("#undo-status-message");

    if (undoLink.length === 0) {
      console.error("Undo link not found");
      return;
    }

    const undoUrl = undoLink.attr("href");
    if (!undoUrl) {
      console.error("Undo URL not found");
      return;
    }

    // Show loading state
    button.prop("disabled", true);
    statusMessage.hide();

    $.ajax({
      url: undoUrl,
      method: "POST",
      dataType: "json",
      success: (response) => {
        if (response.success) {
          // Show success message in modal div
          statusMessage
            .removeClass("alert-warning")
            .addClass("alert-success")
            .html(
              `<i class="fa fa-check"></i> ${response.message || "Transfer order has been undone successfully."}`,
            )
            .show();

          // Close modal after short delay for success message
          setTimeout(() => {
            $("#undoConfirmationModal").modal("hide");
          }, 1500);

          // // Reload the page to show updated status
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          // Show error message in modal div
          statusMessage
            .removeClass("alert-success")
            .addClass("alert-warning")
            .html(
              `<i class="fa fa-exclamation-triangle"></i> ${response.message || "Failed to undo transfer order."}`,
            )
            .show();

          // Close modal after delay for error message (longer timeout since it's an error)
          setTimeout(() => {
            $("#undoConfirmationModal").modal("hide");
          }, 3000);
        }
      },
      error: (xhr) => {
        let errorMessage =
          "An error occurred while undoing the transfer order.";

        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message;
        } else if (xhr.status === 404) {
          errorMessage = "Transfer order not found.";
        } else if (xhr.status === 422) {
          errorMessage = "Invalid data provided.";
        } else if (xhr.status === 403) {
          errorMessage =
            "You do not have permission to undo the transfer order.";
        }

        // Show error message in modal div
        statusMessage
          .removeClass("alert-success")
          .addClass("alert-warning")
          .html(`<i class="fa fa-exclamation-triangle"></i> ${errorMessage}`)
          .show();

        // Close modal after delay for error message
        setTimeout(() => {
          $("#undoConfirmationModal").modal("hide");
        }, 3000);
      },
      complete: () => {
        // Reset button state
        button.prop("disabled", false);
      },
    });
  }

  private handleAddNoteSubmission(): void {
    const form = $("#add-note-form");
    const submitBtn = $("#commit");
    const statusMessage = $("#add-note-status-message");
    const formData = form.serialize();

    // Validate form
    const noteText = $('#add-note-form textarea[name="note"]').val();
    if (!noteText || noteText.toString().trim() === "") {
      statusMessage
        .removeClass("alert-success")
        .addClass("alert-warning")
        .html('<i class="fa fa-exclamation-triangle"></i> Please enter a note.')
        .show();
      return;
    }

    // Show loading state
    submitBtn.prop("disabled", true).text("Adding...");
    statusMessage.hide();

    $.ajax({
      url: form.attr("action"),
      method: "POST",
      data: formData,
      dataType: "json",
      success: (response) => {
        if (response.success) {
          // Show success message with toastr
          Toastr.success(response.message, "Success");

          // Add the new note to the table
          this.addNoteToTable(response.note);

          // Clear form and close modal
          (form[0] as HTMLFormElement).reset();
          $("#addNote").modal("hide");
        } else {
          statusMessage
            .removeClass("alert-success")
            .addClass("alert-warning")
            .html(
              `<i class="fa fa-exclamation-triangle"></i> ${response.message}`,
            )
            .show();
        }
      },
      error: (xhr) => {
        let errorMessage = "An error occurred while adding the note.";

        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message;
        } else if (xhr.status === 422) {
          errorMessage = "Invalid data provided.";
        } else if (xhr.status === 403) {
          errorMessage = "You do not have permission to add notes.";
        }

        statusMessage
          .removeClass("alert-success")
          .addClass("alert-warning")
          .html(`<i class="fa fa-exclamation-triangle"></i> ${errorMessage}`)
          .show();
      },
      complete: () => {
        // Reset button state
        submitBtn.prop("disabled", false).text("Add");
      },
    });
  }

  private handleNoteDeletion(
    actionUrl: string,
    method: string,
    noteId: number,
  ): void {
    $.ajax({
      url: actionUrl,
      method: method,
      dataType: "json",
      success: (response) => {
        if (response.success) {
          // Show success message with toastr
          Toastr.success(response.message, "Success");

          // Remove the note from the table
          this.removeNoteFromTable(noteId);
        } else {
          swal({
            title: "Error",
            text: response.message,
            icon: "error",
          });
        }
      },
      error: (xhr) => {
        let errorMessage = "An error occurred while deleting the note.";

        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message;
        } else if (xhr.status === 404) {
          errorMessage = "Note not found.";
        } else if (xhr.status === 422) {
          errorMessage = "Invalid data provided.";
        } else if (xhr.status === 403) {
          errorMessage = "You do not have permission to delete this note.";
        }

        swal({
          title: "Error",
          text: errorMessage,
          icon: "error",
        });
      },
    });
  }

  private addNoteToTable(note: any): void {
    const notesTable = $("#tab-r2 table tbody");
    const newRow = $(`
      <tr class='center-content' data-note-id="${note.id}">
        <td>${note.created_at_formatted}</td>
        <td style='text-align: left !important;'>
          <span class="hover-cursor-pointer" title="${note.note}" data-toggle="tooltip" data-placement="top">
            ${note.note.length > 80 ? note.note.substring(0, 80) + "..." : note.note}
          </span>
        </td>
        <td>
          ${note.important ? '<i class="fa fa-check text-danger"></i>' : ""}
        </td>
        <td>${note.user_name || "-"}</td>
        <td class='sn-icons'>
          <a href="#/" class="fa fa-trash-o fa-2x delete-icon deleteRecord" title="Delete" data-toggle="tooltip" data-note-id="${note.id}" data-delete-message="This operation will delete the current note"></a>
          <a href="${window.location.pathname}/notes/${note.id}" method="delete" style="display: none" data-method="delete" data-remote="true"></a>
        </td>
      </tr>
    `);

    // Add to the top of the table (most recent first)
    notesTable.prepend(newRow);

    // Initialize tooltips for the new row
    newRow.find('[data-toggle="tooltip"]').tooltip();
  }

  private removeNoteFromTable(noteId: number): void {
    $(`tr[data-note-id="${noteId}"]`).fadeOut(300, function () {
      $(this).remove();
    });
  }

  private handleEosSyncRetry(e: JQuery.ClickEvent): void {
    const $button = $(e.currentTarget);
    const $form = $button.closest("form");
    const formAction = $form.attr("action");
    const formData = $form.serialize();

    // Show loading state
    $button.prop("disabled", true).text("Retrying...");

    $.ajax({
      url: formAction,
      method: "POST",
      data: formData,
      dataType: "json",
      success: (response) => {
        // Hide the retry button and show success message
        $button
          .closest("td")
          .html(
            '<span class="text-success"><i class="fa fa-check-circle"></i> ' +
              response.message +
              "</span>",
          );

        // Show toast notification
        if (typeof Toastr !== "undefined") {
          Toastr.success(response.message, "EOS Sync Retry", {
            timeOut: 5000,
            extendedTimeOut: 2000,
            progressBar: true,
            positionClass: "toast-top-right",
          });
        } else if (typeof ($ as any).notify !== "undefined") {
          // Fallback to jQuery notify if available
          ($ as any).notify(response.message, {
            type: "success",
            delay: 5000,
            placement: {
              from: "top",
              align: "right",
            },
          });
        } else {
          // Last resort fallback to alert
          alert(response.message);
        }

        // Optionally refresh the modal content after a delay to show updated status
        setTimeout(() => {
          // Trigger a refresh of the EOS status modal if needed
          // This could reload the modal content to show updated sync status
          if (typeof (window as any).refreshEosStatus === "function") {
            (window as any).refreshEosStatus();
          }
        }, 2000);
      },
      error: (xhr) => {
        let errorMessage = "An error occurred while retrying EOS sync.";

        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message;
        } else if (xhr.status === 404) {
          errorMessage = "Transfer order not found.";
        } else if (xhr.status === 422) {
          errorMessage = "Invalid data provided.";
        } else if (xhr.status === 403) {
          errorMessage = "You do not have permission to retry EOS sync.";
        }

        // Show error message
        if (typeof Toastr !== "undefined") {
          Toastr.error(errorMessage, "EOS Sync Retry Error", {
            timeOut: 5000,
            extendedTimeOut: 2000,
            progressBar: true,
            positionClass: "toast-top-right",
          });
        } else {
          alert("Error: " + errorMessage);
        }

        $button.prop("disabled", false).text("Retry");
      },
    });
  }

  private setupMergeHandlers(): void {
    $("#mergeBtn").on("click", (e) => {
      e.preventDefault();
      const selectedTransfers: string[] = [];
      $(".transfer:checked").each(function () {
        selectedTransfers.push($(this).val() as string);
      });

      if (selectedTransfers.length < 2) {
        if (typeof Toastr !== "undefined") {
          Toastr.warning(
            "Please select at least 2 transfers to merge.",
            "Merge Transfer Orders",
            {
              timeOut: 4000,
              extendedTimeOut: 2000,
              progressBar: true,
              positionClass: "toast-top-right",
            },
          );
        } else {
          alert("Please select at least 2 transfers to merge.");
        }
        return;
      }

      $("#selectedTransfersSpan").text(selectedTransfers.join(", "));

      const baseTransferOptions = selectedTransfers
        .map((id) => {
          return `<option value="${id}">Transfer #${id}</option>`;
        })
        .join("");
      $("#base_transfer").html(baseTransferOptions);

      $(".mergeModal").modal("show");
    });

    $(".mergeModal form").on("submit", (e) => {
      e.preventDefault();

      const selectedTransfers: string[] = [];
      $(".transfer:checked").each(function () {
        selectedTransfers.push($(this).val() as string);
      });

      const baseTransfer = $("#base_transfer").val() as string;

      if (!baseTransfer) {
        if (typeof Toastr !== "undefined") {
          Toastr.warning(
            "Please select a base transfer to merge into.",
            "Merge Transfer Orders",
            {
              timeOut: 4000,
              extendedTimeOut: 2000,
              progressBar: true,
              positionClass: "toast-top-right",
            },
          );
        } else {
          alert("Please select a base transfer to merge into.");
        }
        return;
      }

      // Show loading state
      const submitBtn = $(e.currentTarget).find('button[type="submit"]');
      const originalText = submitBtn.text();
      submitBtn.prop("disabled", true).text("Merging...");

      // Make AJAX request
      $.ajax({
        url: this.mergeUrl,
        type: "POST",
        data: {
          base_transfer: baseTransfer,
          selected_transfers: selectedTransfers.join(","),
        },
        success: (response: any) => {
          if (response.success) {
            if (typeof Toastr !== "undefined") {
              Toastr.success(response.message, "Merge Transfer Orders", {
                timeOut: 5000,
                extendedTimeOut: 2000,
                progressBar: true,
                positionClass: "toast-top-right",
              });
            } else {
              alert(response.message);
            }
            // Refresh the page to show updated data after a short delay
            setTimeout(() => {
              location.reload();
            }, 1500);
          } else {
            if (typeof Toastr !== "undefined") {
              Toastr.error(
                "Error: " + response.message,
                "Merge Transfer Orders",
                {
                  timeOut: 6000,
                  extendedTimeOut: 2000,
                  progressBar: true,
                  positionClass: "toast-top-right",
                },
              );
            } else {
              alert("Error: " + response.message);
            }
          }
        },
        error: (xhr: any) => {
          const response = JSON.parse(xhr.responseText);
          const errorMessage = response.message || "Failed to merge transfers";
          if (typeof Toastr !== "undefined") {
            Toastr.error("Error: " + errorMessage, "Merge Transfer Orders", {
              timeOut: 6000,
              extendedTimeOut: 2000,
              progressBar: true,
              positionClass: "toast-top-right",
            });
          } else {
            alert("Error: " + errorMessage);
          }
        },
        complete: () => {
          // Reset button state
          submitBtn.prop("disabled", false).text(originalText);
          $(".mergeModal").modal("hide");
        },
      });
    });
  }

  private setupButtonClickPrevention(): void {
    // Prevent multiple clicks on all buttons, links, and form submissions
    $(document).on(
      "click",
      'button, input[type="submit"], input[type="button"], .btn, a[href="#"], .deleteRecord, .delete-icon',
      (e) => {
        const $element = $(e.currentTarget);

        // Skip if already disabled or if it's a checkbox/radio
        if (
          $element.is(":disabled") ||
          $element.is('input[type="checkbox"]') ||
          $element.is('input[type="radio"]')
        ) {
          return;
        }

        // Skip if it's a dropdown toggle button (should not be disabled)
        if (
          $element.hasClass("dropdown-toggle") ||
          $element.attr("data-toggle") === "dropdown"
        ) {
          return;
        }

        // Skip if it's a modal close button or navigation link
        if (
          $element.hasClass("close") ||
          $element.hasClass("carton-print-labels-icon") ||
          $element.attr("data-dismiss") === "modal" ||
          ($element.attr("href") &&
            !$element.attr("href").startsWith("#") &&
            $element.attr("href") !== "#")
        ) {
          return;
        }

        // Skip if it's a form submission (let the form handler manage it)
        if (
          $element.is('input[type="submit"]') ||
          $element.closest("form").length > 0
        ) {
          return;
        }

        // Skip if it's a delete record (let the delete handler manage it)
        if (
          $element.hasClass("deleteRecord") ||
          $element.hasClass("delete-icon")
        ) {
          return;
        }

        // Simply disable the element for other buttons
        $element.prop("disabled", true);

        // Re-enable after a delay to prevent permanent disabling in case of errors
        setTimeout(() => {
          $element.prop("disabled", false);
        }, 5000); // 5 second timeout
      },
    );

    // Re-enable the Reports button when any modal is closed
    $(document).on("hidden.bs.modal", ".modal", () => {
      const $reportsButton = $("#dropdownMenuReports");
      if ($reportsButton.length) {
        $reportsButton.prop("disabled", false);
      }
    });
  }
}

export function LoadPage(): void {
  // Initialize for both index and show pages
  InitPage("transfer_orders", () => {
    const transferId = (window as any).transferId;
    const tenantCode = (window as any).tenant_code;
    const transferTenantCodes =
      (window as any).transfer_tenant_codes || (tenantCode ? [tenantCode] : []);
    const loadPalletsUrl = (window as any).load_pallets_url;
    const loadCartonsItemsUrl = (window as any).load_cartons_items_url;
    const selectedContainers = (window as any).selectedContainers || [];
    const mergeUrl = (window as any).merge_url;

    const config: TransferOrderConfig = {
      transferId,
      tenantCode,
      transferTenantCodes,
      loadPalletsUrl,
      loadCartonsItemsUrl,
      selectedContainers,
      mergeUrl,
    };

    const ntoTransferOrders = new NTOTransferOrders(config);
    ntoTransferOrders.init();
  });
}
